import React, { useCallback, useEffect, useRef, useState } from "react";
import { AiFillDelete, AiOutlineEdit, AiOutlineFileAdd, AiOutlineQuestionCircle } from "react-icons/ai";
import Navbaradmin from "../navbar";
import Sidebaradmin from "../Sidebar";
import QuestionTable from "./QuestionTable";
import SearchBar from "./SearchBar";
import EditQuestionForm from "./AdminEditQuestionForm";
import AddQuestionForm from "./AddQuestionForm";
import DeletedList from "./DeletedList";

export default function Adminpractice() {
  const [searchQuery, setSearchQuery] = useState("");
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [addQuestionData, setAddQuestionData] = useState({});
  const [editFormData, setEditFormData] = useState({});
  const [questions, setQuestions] = useState([]);
  const [deletedQuestions, setDeletedQuestions] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedModel, setSelectedModel] = useState("");
  const [limit, setLimit] = useState(10);
  const [searchType, setSearchType] = useState("name");
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    model: "",
    searchType: "name",
    limit: 10,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("questions");
  const questionsCache = useRef(new Map());
  const activeRequest = useRef(null);

  const fetchQuestions = useCallback(async (nextPage = page, filters = appliedFilters, options = {}) => {
    const pageSize = Math.min(Math.max(Number(filters.limit) || 10, 1), 50);
    const requestFilters = {
      search: filters.search || "",
      model: filters.model || "",
      searchType: filters.searchType || "name",
      limit: pageSize,
    };
    const cacheKey = JSON.stringify({ page: nextPage, ...requestFilters });
    const fullResultCacheKey = JSON.stringify({
      search: requestFilters.search,
      model: requestFilters.model,
      searchType: requestFilters.searchType,
      limit: pageSize,
      fullResult: true,
    });

    if (activeRequest.current) {
      activeRequest.current.abort();
      activeRequest.current = null;
    }

    if (!options.force && questionsCache.current.has(cacheKey)) {
      const cached = questionsCache.current.get(cacheKey);
      setQuestions(cached.questions);
      setTotalPages(cached.totalPages);
      setLoading(false);
      return;
    }

    if (!options.force && questionsCache.current.has(fullResultCacheKey)) {
      const cached = questionsCache.current.get(fullResultCacheKey);
      const start = (nextPage - 1) * pageSize;
      setQuestions(cached.questions.slice(start, start + pageSize));
      setTotalPages(cached.totalPages);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    activeRequest.current = controller;
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        page: String(nextPage),
        limit: String(pageSize),
        model: requestFilters.model,
        searchType: requestFilters.searchType,
        search: requestFilters.search,
      });

      const response = await fetch(`/api/admin/search?${queryParams.toString()}`, {
        signal: controller.signal,
      });
      const data = await response.json();

      if (response.ok) {
        const rawQuestions = Array.isArray(data) ? data : data.data || [];
        const totalItems = data.totalItems || data.totalCount || data.pagination?.totalItems || data.pagination?.totalCount;
        const hasPaginationMeta = Boolean(data.totalPages || data.pagination?.totalPages || totalItems);
        const nextTotalPages = hasPaginationMeta
          ? data.totalPages || data.pagination?.totalPages || Math.max(1, Math.ceil(totalItems / pageSize))
          : Math.max(1, Math.ceil(rawQuestions.length / pageSize));
        const nextQuestions =
          !hasPaginationMeta && rawQuestions.length > pageSize
            ? rawQuestions.slice((nextPage - 1) * pageSize, nextPage * pageSize)
            : rawQuestions;

        setQuestions(nextQuestions);
        setTotalPages(nextTotalPages);
        questionsCache.current.set(cacheKey, {
          questions: nextQuestions,
          totalPages: nextTotalPages,
        });

        if (!hasPaginationMeta && rawQuestions.length > pageSize) {
          questionsCache.current.set(fullResultCacheKey, {
            questions: rawQuestions,
            totalPages: nextTotalPages,
          });
        }
      } else {
        throw new Error(data.error || "Failed to fetch questions");
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        setError(error.message);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
        activeRequest.current = null;
      }
    }
  }, [appliedFilters, page]);

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleModelChange = (event) => {
    setSelectedModel(event.target.value);
  };

  const handleSearchClick = (nextSearch = searchQuery, nextLimit = limit) => {
    const nextFilters = {
      search: nextSearch || "",
      model: selectedModel,
      searchType,
      limit: Math.min(Math.max(Number(nextLimit) || 10, 1), 50),
    };

    setAppliedFilters(nextFilters);
    setPage(1);
    return Promise.resolve();
  };

  const handleEditClick = (question) => {
    if (question) {
      setEditingQuestion(question);
      setEditFormData(question);
      setActiveTab("edit");
    }
  };

  const handleDeleteClick = (id) => {
    setQuestions((prevQuestions) => prevQuestions.filter((q) => q.id !== id));
    const deletedQuestion = questions.find((q) => q.id === id);
    if (deletedQuestion) {
      setDeletedQuestions((prevDeleted) => [...prevDeleted, { ...deletedQuestion, deletedAt: Date.now() }]);
    }
  };

  const handleRestoreQuestion = (id) => {
    setDeletedQuestions((prevDeleted) => prevDeleted.filter((q) => q.id !== id));
    const restoredQuestion = deletedQuestions.find((q) => q.id === id);
    if (restoredQuestion) {
      setQuestions((prevQuestions) => [...prevQuestions, restoredQuestion]);
    }
  };

  const handleSaveChanges = () => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => (q._id === editingQuestion?._id ? { ...q, ...editFormData } : q))
    );
    questionsCache.current.clear();
    setEditingQuestion(null);
    setActiveTab("questions");
    fetchQuestions(page, appliedFilters, { force: true });
  };

  const handleAddNewQuestion = () => {
    const newQuestion = { id: Date.now(), ...addQuestionData };
    setQuestions((prevQuestions) => [...prevQuestions, newQuestion]);
    setAddQuestionData({});
    setActiveTab("questions");
  };

  useEffect(() => {
    fetchQuestions(page, appliedFilters);
  }, [appliedFilters, fetchQuestions, page]);

  const sections = new Set(questions.map((question) => question.modelName).filter(Boolean)).size;

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <div className="flex min-h-screen flex-col md:flex-row">
        <Sidebaradmin />
        <div className="flex min-w-0 flex-1 flex-col">
          <Navbaradmin />

          <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
            <section className="mb-6 flex flex-col justify-between gap-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm lg:flex-row lg:items-center">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-sky-600">Question management</p>
                <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
                  Admin Questions
                </h1>
                <p className="mt-1 max-w-2xl text-sm text-slate-500">
                  Search, edit, add, delete, and restore practice questions across PTE modules.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3 sm:min-w-[420px]">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-semibold uppercase text-slate-500">Loaded</p>
                  <p className="mt-1 text-2xl font-bold text-slate-950">{questions.length}</p>
                </div>
                <div className="rounded-lg border border-sky-100 bg-sky-50 p-3">
                  <p className="text-xs font-semibold uppercase text-sky-700">Sections</p>
                  <p className="mt-1 text-2xl font-bold text-sky-800">{sections}</p>
                </div>
                <div className="rounded-lg border border-amber-100 bg-amber-50 p-3">
                  <p className="text-xs font-semibold uppercase text-amber-700">Deleted</p>
                  <p className="mt-1 text-2xl font-bold text-amber-800">{deletedQuestions.length}</p>
                </div>
              </div>
            </section>

            {error && (
              <div className="mb-6 rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
                {error}
              </div>
            )}

            <SearchBar
              searchQuery={searchQuery}
              handleSearch={handleSearch}
              handleSearchClick={handleSearchClick}
              selectedModel={selectedModel}
              handleModelChange={handleModelChange}
              setLimit={setLimit}
              searchType={searchType}
              setSearchType={setSearchType}
              setSearchQuery={setSearchQuery}
              loading={loading}
            />

            <section className="mb-6 rounded-lg border border-slate-200 bg-white p-1.5 shadow-sm">
              <nav className="grid gap-2 sm:flex">
                <button
                  className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition ${
                    activeTab === "questions"
                      ? "bg-slate-950 text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-950"
                  }`}
                  onClick={() => setActiveTab("questions")}
                >
                  <AiOutlineQuestionCircle className="text-lg" />
                  Question Table
                </button>
                <button
                  className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition ${
                    activeTab === "add"
                      ? "bg-slate-950 text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-950"
                  }`}
                  onClick={() => setActiveTab("add")}
                >
                  <AiOutlineFileAdd className="text-lg" />
                  Add Question
                </button>
                <button
                  className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition ${
                    activeTab === "deleted"
                      ? "bg-slate-950 text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-950"
                  }`}
                  onClick={() => setActiveTab("deleted")}
                >
                  <AiFillDelete className="text-lg" />
                  Deleted Questions
                </button>
                {activeTab === "edit" && (
                  <button className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm">
                    <AiOutlineEdit className="text-lg" />
                    Edit Question
                  </button>
                )}
              </nav>
            </section>

            {activeTab === "questions" && (
              <QuestionTable
                questions={questions}
                loading={loading}
                currentPage={page}
                limit={limit}
                handleEditClick={handleEditClick}
                handleDeleteClick={handleDeleteClick}
                searchQuery={searchQuery}
              />
            )}

            {activeTab === "add" && (
              <AddQuestionForm
                formData={addQuestionData}
                handleInputChange={(event) => {
                  const { name, type, checked, value } = event.target;
                  setAddQuestionData({
                    ...addQuestionData,
                    [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
                  });
                }}
                handleAddNewQuestion={handleAddNewQuestion}
              />
            )}

            {activeTab === "edit" && editingQuestion && (
              <EditQuestionForm
                questionId={editingQuestion._id}
                formData={editFormData}
                handleInputChange={(event) => {
                  const { name, type, checked, value } = event.target;
                  setEditFormData({
                    ...editFormData,
                    [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
                  });
                }}
                handleSaveChanges={handleSaveChanges}
                setEditingQuestion={(value) => {
                  setEditingQuestion(value);
                  if (!value) {
                    setActiveTab("questions");
                  }
                }}
              />
            )}

            {activeTab === "deleted" && (
              <DeletedList deletedQuestions={deletedQuestions} restoreQuestion={handleRestoreQuestion} />
            )}

            <section className="mt-6 flex flex-col justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-600">
                Items per page
                <select
                  value={limit}
                  onChange={(event) => {
                    const nextLimit = Number(event.target.value);
                    const nextFilters = { ...appliedFilters, limit: nextLimit };
                    setLimit(nextLimit);
                    setAppliedFilters(nextFilters);
                    setPage(1);
                    questionsCache.current.clear();
                  }}
                  className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </label>

              <div className="flex items-center justify-between gap-3 sm:justify-end">
                <button
                  onClick={() => setPage((current) => Math.max(current - 1, 1))}
                  disabled={page <= 1}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm font-semibold text-slate-600">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((current) => Math.min(current + 1, totalPages))}
                  disabled={page >= totalPages}
                  className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}
